/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package models

import ai.ChartSpec
import ai.ReasonedNarrative
import i18n.StringKey
import i18n.tr
import kotlinx.serialization.Serializable

/**
 * Represents an advice card with financial tips and guidance.
 */
@Serializable
data class AdviceCard(
    val id: String,
    val category: AdviceCardCategory,
    val title: String,
    val content: String,
    val icon: String? = null,
    val priority: Int = 0,
    val isActive: Boolean = true,
    val country: String? = null
) {
    /**
     * Returns the localized title for default cards (by card ID → StringKey).
     * Falls back to the stored [title] for custom/unknown cards.
     */
    val localizedTitle: String
        get() = defaultCardTitleKey[id]?.tr() ?: title

    /**
     * Returns the localized content for default cards (by card ID → StringKey).
     * Falls back to the stored [content] for custom/unknown cards.
     */
    val localizedContent: String
        get() = defaultCardContentKey[id]?.tr() ?: content

    companion object {
        private val defaultCardTitleKey = mapOf(
            "tip-50-30-20" to StringKey.ADVICE_TIP_50_30_20_TITLE,
            "tip-automate-savings" to StringKey.ADVICE_TIP_AUTOMATE_TITLE,
            "tip-24-hour-rule" to StringKey.ADVICE_TIP_24_HOUR_TITLE,
            "goal-emergency-fund" to StringKey.ADVICE_GOAL_EMERGENCY_TITLE,
            "goal-smart" to StringKey.ADVICE_GOAL_SMART_TITLE,
            "goal-visualize" to StringKey.ADVICE_GOAL_VISUALIZE_TITLE,
            "account-tfsa" to StringKey.ADVICE_ACCOUNT_TFSA_TITLE,
            "account-rrsp" to StringKey.ADVICE_ACCOUNT_RRSP_TITLE,
            "account-fhsa" to StringKey.ADVICE_ACCOUNT_FHSA_TITLE,
            "account-401k" to StringKey.ADVICE_ACCOUNT_401K_TITLE,
            "account-roth-ira" to StringKey.ADVICE_ACCOUNT_ROTH_IRA_TITLE,
            "account-trad-ira" to StringKey.ADVICE_ACCOUNT_TRAD_IRA_TITLE,
            "account-hsa" to StringKey.ADVICE_ACCOUNT_HSA_TITLE,
            "encourage-compound" to StringKey.ADVICE_ENCOURAGE_COMPOUND_TITLE,
            "encourage-small-wins" to StringKey.ADVICE_ENCOURAGE_SMALL_WINS_TITLE,
            "encourage-doing-great" to StringKey.ADVICE_ENCOURAGE_DOING_GREAT_TITLE,
            "behavioral-lifestyle-creep" to StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_TITLE,
            "behavioral-pay-yourself" to StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_TITLE,
            "behavioral-unsubscribe" to StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_TITLE,
        )
        private val defaultCardContentKey = mapOf(
            "tip-50-30-20" to StringKey.ADVICE_TIP_50_30_20_CONTENT,
            "tip-automate-savings" to StringKey.ADVICE_TIP_AUTOMATE_CONTENT,
            "tip-24-hour-rule" to StringKey.ADVICE_TIP_24_HOUR_CONTENT,
            "goal-emergency-fund" to StringKey.ADVICE_GOAL_EMERGENCY_CONTENT,
            "goal-smart" to StringKey.ADVICE_GOAL_SMART_CONTENT,
            "goal-visualize" to StringKey.ADVICE_GOAL_VISUALIZE_CONTENT,
            "account-tfsa" to StringKey.ADVICE_ACCOUNT_TFSA_CONTENT,
            "account-rrsp" to StringKey.ADVICE_ACCOUNT_RRSP_CONTENT,
            "account-fhsa" to StringKey.ADVICE_ACCOUNT_FHSA_CONTENT,
            "account-401k" to StringKey.ADVICE_ACCOUNT_401K_CONTENT,
            "account-roth-ira" to StringKey.ADVICE_ACCOUNT_ROTH_IRA_CONTENT,
            "account-trad-ira" to StringKey.ADVICE_ACCOUNT_TRAD_IRA_CONTENT,
            "account-hsa" to StringKey.ADVICE_ACCOUNT_HSA_CONTENT,
            "encourage-compound" to StringKey.ADVICE_ENCOURAGE_COMPOUND_CONTENT,
            "encourage-small-wins" to StringKey.ADVICE_ENCOURAGE_SMALL_WINS_CONTENT,
            "encourage-doing-great" to StringKey.ADVICE_ENCOURAGE_DOING_GREAT_CONTENT,
            "behavioral-lifestyle-creep" to StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_CONTENT,
            "behavioral-pay-yourself" to StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_CONTENT,
            "behavioral-unsubscribe" to StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_CONTENT,
        )
    }
}

/**
 * Categories for advice cards.
 */
@Serializable
enum class AdviceCardCategory {
    SAVINGS_TIPS,
    GOAL_SETTING,
    ACCOUNTS,
    ENCOURAGEMENT,
    BEHAVIORAL;

    companion object {
        fun fromString(value: String): AdviceCardCategory {
            return when (value.lowercase()) {
                "savings_tips" -> SAVINGS_TIPS
                "goal_setting" -> GOAL_SETTING
                "accounts" -> ACCOUNTS
                "encouragement" -> ENCOURAGEMENT
                "behavioral" -> BEHAVIORAL
                else -> SAVINGS_TIPS
            }
        }
    }

    override fun toString(): String {
        return name.lowercase()
    }

    val displayName: String
        get() = when (this) {
            SAVINGS_TIPS -> StringKey.ADVICE_CAT_SAVINGS_TIPS.tr()
            GOAL_SETTING -> StringKey.ADVICE_CAT_GOAL_SETTING.tr()
            ACCOUNTS -> StringKey.ADVICE_CAT_ACCOUNTS.tr()
            ENCOURAGEMENT -> StringKey.ADVICE_CAT_ENCOURAGEMENT.tr()
            BEHAVIORAL -> StringKey.ADVICE_CAT_BEHAVIORAL.tr()
        }

    val color: String
        get() = when (this) {
            SAVINGS_TIPS -> "#00d9ff"     // Cyan
            GOAL_SETTING -> "#10B981"     // Green
            ACCOUNTS -> "#F59E0B"         // Amber
            ENCOURAGEMENT -> "#8B5CF6"    // Purple
            BEHAVIORAL -> "#EC4899"       // Pink
        }
}

/**
 * User interaction status with an advice card.
 */
@Serializable
data class UserAdviceCard(
    val id: String,
    val userId: String,
    val cardId: String,
    val isPinned: Boolean = false,
    val isDismissed: Boolean = false,
    val viewedAt: String? = null,
    val createdAt: String? = null
)

/**
 * Advice card with user interaction status combined.
 */
@Serializable
data class AdviceCardWithStatus(
    val card: AdviceCard,
    val isPinned: Boolean = false,
    val isDismissed: Boolean = false,
    val viewedAt: String? = null
)

// ============================================================================
// PERSONALIZED ADVICE CARDS (Dynamic, data-driven)
// ============================================================================

/**
 * Types of personalized advice cards generated from user data analysis.
 */
@Serializable
enum class PersonalizedCardType {
    CATEGORY_ALERT,      // High spending in a category
    SPENDING_SPIKE,      // Category spending increased significantly
    SAVINGS_PERSONAL_BEST, // New personal best savings rate
    SAVINGS_IMPROVED,    // Month-over-month improvement
    SAVINGS_DROPPED,     // Savings rate declined
    GOAL_ON_TRACK,       // Goal progressing well
    GOAL_BEHIND,         // Goal behind schedule
    GOAL_MILESTONE,      // Goal reached 25%, 50%, 75%
    GOAL_ALMOST_COMPLETE, // Goal > 90%
    WEEKEND_SPENDING,    // High weekend spending pattern
    SUBSCRIPTION_CREEP,  // Subscriptions growing
    BIGGEST_OPPORTUNITY, // Highest impact category to reduce
    NEW_USER_WELCOME,    // Welcome card for new users
    NO_GOALS_PROMPT,     // Encourage goal creation
    GETTING_STARTED,     // Getting started guide cards
    CATEGORY_STREAK,     // Stayed under budget for N months
    MONTH_COMPARISON,    // Month-over-month spending change
    TOP_MERCHANT,        // Top merchant this month
    UPCOMING_BILLS,      // Recurring bills coming up
    BUDGET_VS_ACTUAL,    // Budget progress warning
    CATEGORY_RANK_SHIFT, // Category moved up in spending rank
    GOAL_PROJECTION,     // Projected goal completion date
    PACE_TRENDING_OVER,  // Mid-month: current pace projects significantly over last month
    PACE_ON_TRACK_BETTER; // Mid-month: current pace projects significantly under last month

    val iconName: String
        get() = when (this) {
            CATEGORY_ALERT -> "warning"
            SPENDING_SPIKE -> "trending_up"
            SAVINGS_PERSONAL_BEST -> "star"
            SAVINGS_IMPROVED -> "thumb_up"
            SAVINGS_DROPPED -> "trending_down"
            GOAL_ON_TRACK -> "check_circle"
            GOAL_BEHIND -> "schedule"
            GOAL_MILESTONE -> "emoji_events"
            GOAL_ALMOST_COMPLETE -> "celebration"
            WEEKEND_SPENDING -> "weekend"
            SUBSCRIPTION_CREEP -> "subscriptions"
            BIGGEST_OPPORTUNITY -> "lightbulb"
            NEW_USER_WELCOME -> "waving_hand"
            NO_GOALS_PROMPT -> "flag"
            GETTING_STARTED -> "school"
            CATEGORY_STREAK -> "local_fire_department"
            MONTH_COMPARISON -> "compare_arrows"
            TOP_MERCHANT -> "storefront"
            UPCOMING_BILLS -> "calendar_today"
            BUDGET_VS_ACTUAL -> "account_balance"
            CATEGORY_RANK_SHIFT -> "swap_vert"
            GOAL_PROJECTION -> "timeline"
            PACE_TRENDING_OVER -> "speed"
            PACE_ON_TRACK_BETTER -> "trending_down"
        }

    val iconColor: String
        get() = when (this) {
            CATEGORY_ALERT, SPENDING_SPIKE, SAVINGS_DROPPED, GOAL_BEHIND -> "#FF6B6B"  // Red/warning
            SAVINGS_PERSONAL_BEST, GOAL_MILESTONE, GOAL_ALMOST_COMPLETE -> "#10B981"  // Green/success
            SAVINGS_IMPROVED, GOAL_ON_TRACK -> "#00d9ff"  // Cyan/positive
            WEEKEND_SPENDING, SUBSCRIPTION_CREEP -> "#F59E0B"  // Amber/attention
            BIGGEST_OPPORTUNITY -> "#8B5CF6"  // Purple/opportunity
            NEW_USER_WELCOME, NO_GOALS_PROMPT -> "#EC4899"  // Pink/friendly
            GETTING_STARTED -> "#00d9ff"  // Cyan/guide
            CATEGORY_STREAK -> "#10B981"  // Green/achievement
            MONTH_COMPARISON -> "#00d9ff"  // Cyan/info
            TOP_MERCHANT -> "#8B5CF6"  // Purple
            UPCOMING_BILLS -> "#F59E0B"  // Amber/attention
            BUDGET_VS_ACTUAL -> "#FF6B6B"  // Red/warning
            CATEGORY_RANK_SHIFT -> "#F59E0B"  // Amber
            GOAL_PROJECTION -> "#00d9ff"  // Cyan/positive
            PACE_TRENDING_OVER -> "#F59E0B"  // Amber/attention
            PACE_ON_TRACK_BETTER -> "#10B981"  // Green/success
        }
}

/**
 * Action types for personalized advice cards.
 */
@Serializable
enum class CardActionType {
    NAVIGATE_CATEGORY,   // Navigate to transactions filtered by category
    NAVIGATE_GOAL,       // Navigate to a specific goal
    NAVIGATE_TRANSACTIONS, // Navigate to all transactions
    CREATE_GOAL,         // Open goal creation
    SHARE,               // Share achievement
    DISMISS,             // Just dismiss the card
    NAVIGATE_GUIDE,      // Navigate to the guide page
    REVIEW_SUBSCRIPTIONS, // Open subscription review modal
    NONE                 // No action
}

/**
 * A personalized advice card generated from user data analysis.
 * These are dynamic and change based on user's spending patterns, goals, etc.
 */
@Serializable
data class PersonalizedAdviceCard(
    val id: String,
    val type: PersonalizedCardType,
    val priority: Int,  // Higher = more important (0-100)
    val title: String,
    val icon: String,
    val iconColor: String,
    val bodyLines: List<String>,  // Multiple lines of text
    val actionText: String?,
    val actionType: CardActionType = CardActionType.NONE,
    val actionData: Map<String, String> = emptyMap(),  // e.g., {"category": "FOOD_AND_DRINK"}
    val metrics: CardMetrics? = null,  // The actual numbers used to generate this card
    // Wiz 3R AI fields — all optional, default null/false so existing cards
    // serialize and render identically when the AI personalizer is off.
    val narrative: ReasonedNarrative? = null,
    val chart: ChartSpec? = null,
    val canDrillDown: Boolean = false,
    // Teaser for standard (non-Pro) users: the card carries a deterministic
    // Reveal line (reason/realize are empty), and the client shows a Pro-locked
    // "See why" that routes to the paywall instead of opening the detail/chat.
    // Distinct from canDrillDown, which is the real (Pro) drill-down.
    val revealTeaser: Boolean = false,
)

/**
 * Metrics associated with a personalized card (for debugging/transparency).
 */
@Serializable
data class CardMetrics(
    val amount: Double? = null,
    val percentOfIncome: Double? = null,
    val percentChange: Double? = null,
    val previousAmount: Double? = null,
    val savingsRate: Double? = null,
    val previousSavingsRate: Double? = null,
    /** Goal completion in PERCENT form (0-100), not fraction (0-1).
     *  e.g. a goal with $11,000 saved of a $45,000 target → 24.4.
     *  Consumers must NOT multiply by 100 again. */
    val goalProgress: Double? = null,
    val targetDate: String? = null,
    val projectedDate: String? = null,
    val category: String? = null,
    /** Actual / planned × 100 — set by BUDGET_VS_ACTUAL cards. 141.6% means
     *  the user spent 141.6% of the budgeted amount (i.e. 41.6% over). */
    val percentOfBudget: Double? = null,
    /** Planned amount the actual was compared against. */
    val plannedAmount: Double? = null,
)

/**
 * Response wrapper for personalized advice cards API.
 */
@Serializable
data class PersonalizedCardsResponse(
    val cards: List<PersonalizedAdviceCard>,
    val generatedAt: String,
    val userDataDays: Int = 0,  // Number of days of data used
    // True when the AI prose was served from an older bundle while a fresh one
    // regenerates in the background. The factual Reveal line is rebuilt from
    // live data either way, so only the Reason/Realize framing is behind.
    // Clients re-fetch shortly after seeing this so the view catches up on its
    // own. Defaulted so older clients decode this response unchanged.
    val refreshing: Boolean = false
)

/**
 * Default advice cards to seed the database.
 */
object DefaultAdviceCards {
    val cards: List<AdviceCard>
        get() = listOf(
        // Savings Tips
        AdviceCard(
            id = "tip-50-30-20",
            category = AdviceCardCategory.SAVINGS_TIPS,
            title = StringKey.ADVICE_TIP_50_30_20_TITLE.tr(),
            content = StringKey.ADVICE_TIP_50_30_20_CONTENT.tr(),
            icon = "pie_chart",
            priority = 1
        ),
        AdviceCard(
            id = "tip-automate-savings",
            category = AdviceCardCategory.SAVINGS_TIPS,
            title = StringKey.ADVICE_TIP_AUTOMATE_TITLE.tr(),
            content = StringKey.ADVICE_TIP_AUTOMATE_CONTENT.tr(),
            icon = "autorenew",
            priority = 2
        ),
        AdviceCard(
            id = "tip-24-hour-rule",
            category = AdviceCardCategory.SAVINGS_TIPS,
            title = StringKey.ADVICE_TIP_24_HOUR_TITLE.tr(),
            content = StringKey.ADVICE_TIP_24_HOUR_CONTENT.tr(),
            icon = "schedule",
            priority = 3
        ),

        // Goal Setting
        AdviceCard(
            id = "goal-emergency-fund",
            category = AdviceCardCategory.GOAL_SETTING,
            title = StringKey.ADVICE_GOAL_EMERGENCY_TITLE.tr(),
            content = StringKey.ADVICE_GOAL_EMERGENCY_CONTENT.tr(),
            icon = "shield",
            priority = 1
        ),
        AdviceCard(
            id = "goal-smart",
            category = AdviceCardCategory.GOAL_SETTING,
            title = StringKey.ADVICE_GOAL_SMART_TITLE.tr(),
            content = StringKey.ADVICE_GOAL_SMART_CONTENT.tr(),
            icon = "target",
            priority = 2
        ),
        AdviceCard(
            id = "goal-visualize",
            category = AdviceCardCategory.GOAL_SETTING,
            title = StringKey.ADVICE_GOAL_VISUALIZE_TITLE.tr(),
            content = StringKey.ADVICE_GOAL_VISUALIZE_CONTENT.tr(),
            icon = "visibility",
            priority = 3
        ),

        // Account Types (Canada-specific)
        AdviceCard(
            id = "account-tfsa",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_TFSA_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_TFSA_CONTENT.tr(),
            icon = "account_balance",
            priority = 1,
            country = "CA"
        ),
        AdviceCard(
            id = "account-rrsp",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_RRSP_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_RRSP_CONTENT.tr(),
            icon = "elderly",
            priority = 2,
            country = "CA"
        ),
        AdviceCard(
            id = "account-fhsa",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_FHSA_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_FHSA_CONTENT.tr(),
            icon = "home",
            priority = 3,
            country = "CA"
        ),

        // Account Types (US-specific)
        AdviceCard(
            id = "account-401k",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_401K_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_401K_CONTENT.tr(),
            icon = "account_balance",
            priority = 1,
            country = "US"
        ),
        AdviceCard(
            id = "account-roth-ira",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_ROTH_IRA_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_ROTH_IRA_CONTENT.tr(),
            icon = "savings",
            priority = 2,
            country = "US"
        ),
        AdviceCard(
            id = "account-trad-ira",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_TRAD_IRA_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_TRAD_IRA_CONTENT.tr(),
            icon = "elderly",
            priority = 3,
            country = "US"
        ),
        AdviceCard(
            id = "account-hsa",
            category = AdviceCardCategory.ACCOUNTS,
            title = StringKey.ADVICE_ACCOUNT_HSA_TITLE.tr(),
            content = StringKey.ADVICE_ACCOUNT_HSA_CONTENT.tr(),
            icon = "health_and_safety",
            priority = 4,
            country = "US"
        ),

        // Encouragement
        AdviceCard(
            id = "encourage-compound",
            category = AdviceCardCategory.ENCOURAGEMENT,
            title = StringKey.ADVICE_ENCOURAGE_COMPOUND_TITLE.tr(),
            content = StringKey.ADVICE_ENCOURAGE_COMPOUND_CONTENT.tr(),
            icon = "trending_up",
            priority = 1
        ),
        AdviceCard(
            id = "encourage-small-wins",
            category = AdviceCardCategory.ENCOURAGEMENT,
            title = StringKey.ADVICE_ENCOURAGE_SMALL_WINS_TITLE.tr(),
            content = StringKey.ADVICE_ENCOURAGE_SMALL_WINS_CONTENT.tr(),
            icon = "emoji_events",
            priority = 2
        ),
        AdviceCard(
            id = "encourage-doing-great",
            category = AdviceCardCategory.ENCOURAGEMENT,
            title = StringKey.ADVICE_ENCOURAGE_DOING_GREAT_TITLE.tr(),
            content = StringKey.ADVICE_ENCOURAGE_DOING_GREAT_CONTENT.tr(),
            icon = "star",
            priority = 3
        ),

        // Behavioral
        AdviceCard(
            id = "behavioral-lifestyle-creep",
            category = AdviceCardCategory.BEHAVIORAL,
            title = StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_TITLE.tr(),
            content = StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_CONTENT.tr(),
            icon = "trending_flat",
            priority = 1
        ),
        AdviceCard(
            id = "behavioral-pay-yourself",
            category = AdviceCardCategory.BEHAVIORAL,
            title = StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_TITLE.tr(),
            content = StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_CONTENT.tr(),
            icon = "savings",
            priority = 2
        ),
        AdviceCard(
            id = "behavioral-unsubscribe",
            category = AdviceCardCategory.BEHAVIORAL,
            title = StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_TITLE.tr(),
            content = StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_CONTENT.tr(),
            icon = "notifications_off",
            priority = 3
        )
    )
}
